import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiEdit2, FiImage, FiSend, FiTrash2, FiX } from 'react-icons/fi';
import {
  cDeleteGroupMessage, cEditGroupMessage, cGroupMessages, cSendGroupMessage, cUpload, fullUrl,
} from '../../api';
import { useLanguage } from '../../i18n';
import AudioPlayer from './AudioPlayer';
import Linkify from './Linkify';
import UserAvatar from './UserAvatar';
import VoiceRecorder from './VoiceRecorder';
import { BLUE, timeAgo } from './brand';

/**
 * The **group chat** — polled every 4 seconds like the direct messages.
 *
 * Text + up to 10 pictures + a **voice note**; the sender can edit or delete
 * their own message, the group admins can delete any message, and a plain
 * member can still hide one for themselves (“delete for me”).
 */
export default function GroupChat({ groupId, canManage = false, muted = false }) {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuFor, setMenuFor] = useState(null);
  const fileRef = useRef(null);
  const bottom = useRef(null);

  const scrollDown = () => setTimeout(() => {
    if (bottom.current) bottom.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, 60);

  const load = () => cGroupMessages(groupId, { limit: 60 })
    .then((res) => { setItems(res.items || []); scrollDown(); })
    .catch(() => {});

  useEffect(() => {
    let stopped = false;
    load();
    const tick = async () => {
      if (stopped || document.hidden) return;
      const last = items.length ? items[items.length - 1].id : 0;
      try {
        const res = await cGroupMessages(groupId, { after: last, limit: 60 });
        const fresh = res.items || [];
        if (fresh.length) {
          setItems((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            return [...prev, ...fresh.filter((m) => !seen.has(m.id))];
          });
          scrollDown();
        }
      } catch (e) { /* offline — keep what we have */ }
    };
    const iv = setInterval(tick, 4000);
    return () => { stopped = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, items.length]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body && pending.length === 0) return;
    setBusy(true);
    try {
      const res = await cSendGroupMessage(groupId, { text: body, images: pending });
      setItems((prev) => [...prev, res.message]);
      setText('');
      setPending([]);
      scrollDown();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const sendVoice = async ({ url }) => {
    try {
      const res = await cSendGroupMessage(groupId, { text: '', audio: url });
      setItems((prev) => [...prev, res.message]);
      scrollDown();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const attach = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    try {
      const up = await cUpload(list);
      setPending((prev) => [...prev, ...(up.urls || [])].slice(0, 10));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const saveEdit = async (id) => {
    const body = editText.trim();
    if (!body) return;
    try {
      const res = await cEditGroupMessage(groupId, id, { text: body });
      setItems((prev) => prev.map((m) => (m.id === id ? res.message : m)));
      setEditingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const drop = async (id, forMe = false) => {
    setMenuFor(null);
    try {
      await cDeleteGroupMessage(groupId, id, forMe);
      setItems((prev) => prev.filter((m) => m.id !== id));
      toast.success(t('deletedOk'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  return (
    <div className="flex flex-col">
      <div className="max-h-[52vh] overflow-y-auto px-1 space-y-2">
        {items.length === 0 && (
          <p className="text-xs text-gray-400 py-3">{t('groupMessagePlaceholder')}</p>
        )}
        {items.map((m) => {
          const mine = m.mine;
          const pics = m.images || [];
          return (
            <div key={m.id} className={`flex gap-2 ${mine ? 'flex-row-reverse' : ''}`}>
              {!mine && (
                <UserAvatar user={m.sender} size={32} to={m.sender ? `/u/${m.sender.username}` : null} />
              )}
              <div className={`max-w-[78%] flex flex-col ${mine ? 'items-end' : ''}`}>
                <span className="text-[10px] text-gray-400 px-1">
                  {mine ? t('you') : (m.sender_name || m.sender?.name || m.sender?.username)}
                  {' · '}{timeAgo(m.created_at, lang)}{m.edited_at ? ` · ${t('edited')}` : ''}
                </span>
                <div
                  className={`rounded-2xl px-3 py-2 ${mine ? 'text-white' : 'bg-gray-100 dark:bg-gray-800'}`}
                  style={mine ? { background: BLUE } : undefined}
                >
                  {editingId === m.id ? (
                    <span className="flex items-center gap-1.5">
                      <input
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="rounded-lg px-2 py-1 text-sm text-gray-900"
                      />
                      <button type="button" onClick={() => saveEdit(m.id)} className="text-xs font-bold underline">{t('save')}</button>
                      <button type="button" onClick={() => setEditingId(null)} className="text-xs font-bold underline">{t('cancel')}</button>
                    </span>
                  ) : (
                    <>
                      {pics.length > 0 && (
                        <span className={`grid gap-1 mb-1 ${pics.length > 1 ? 'grid-cols-2' : ''}`}>
                          {pics.map((src) => (
                            <img key={src} src={fullUrl(src)} alt="" className="rounded-xl max-h-56 object-cover" />
                          ))}
                        </span>
                      )}
                      {m.audio && <span className="block"><AudioPlayer src={m.audio} compact /></span>}
                      {m.text && <Linkify text={m.text} className="text-sm whitespace-pre-wrap break-words" />}
                    </>
                  )}
                </div>
                {m.can_manage && editingId !== m.id && (
                  <span className="relative inline-flex items-center gap-2 px-1 pt-0.5 text-[11px] text-gray-400">
                    {mine && (
                      <button
                        type="button"
                        onClick={() => { setEditingId(m.id); setEditText(m.text || ''); }}
                        className="hover:text-gray-700 dark:hover:text-gray-200"
                        aria-label={t('edit')}
                      >
                        <FiEdit2 className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setMenuFor(menuFor === m.id ? null : m.id)}
                      className="hover:text-red-600"
                      aria-label={t('delete')}
                    >
                      <FiTrash2 className="w-3 h-3" />
                    </button>
                    {menuFor === m.id && (
                      <span className={`absolute z-30 mt-4 w-40 rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 p-1 anim-pop ${mine ? 'right-0' : 'left-0'}`}>
                        <button
                          type="button"
                          onClick={() => drop(m.id, false)}
                          className="w-full px-3 py-2 rounded-lg text-xs font-semibold text-left text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          {t('delete')}
                        </button>
                        <button
                          type="button"
                          onClick={() => drop(m.id, true)}
                          className="w-full px-3 py-2 rounded-lg text-xs font-semibold text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          {t('deleteForMe')}
                        </button>
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>

      {muted ? (
        <p className="text-xs text-amber-600 font-semibold py-2">{t('commentsOff')}</p>
      ) : (
        <form onSubmit={send} className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
          {pending.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              {pending.map((src) => (
                <span key={src} className="relative w-14 h-14 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800">
                  <img src={fullUrl(src)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPending((p) => p.filter((x) => x !== src))}
                    className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/55 text-white"
                    aria-label={t('remove')}
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => attach(e.target.files)} />
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              className="p-2.5 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={t('mediaPicture')}
            >
              <FiImage className="w-4 h-4" />
            </button>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
              rows={1}
              placeholder={t('groupMessagePlaceholder')}
              className="flex-1 resize-none rounded-2xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2 text-sm outline-none focus:border-gray-400"
            />
            <VoiceRecorder kind="voice" onUploaded={sendVoice} />
            <button
              type="submit"
              disabled={busy || (!text.trim() && pending.length === 0)}
              data-sfx="send"
              className="p-2.5 rounded-full text-white disabled:opacity-50"
              style={{ background: BLUE }}
              aria-label={t('sendMessage')}
            >
              <FiSend className="w-4 h-4" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
